import React, { useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Select, Option, Tooltip } from '@mm/components';
import { Select as AntdSelect } from 'antd';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { CampaignAudienceAttribute } from '@mm/state/model/CampaignsModel';
import { useAudiencesReducer } from './use-audiences-reducer';
import { useAudienceViewHandlers } from '../../../beta/modules/Audiences/use-audience-view-handlers';
import { MenuItem } from '../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import './AudiencesSelector.pcss';

type Props = {
  className?: string;
  disabled?: boolean;
  label?: string;
  selected?: Option[];
  onClose?: () => void;
  onChange?: (v: Option[]) => void;
  withThreeDotMenu?: boolean;
  placeholder?: string;
  onlyEsAudiences?: boolean;
  singleSelect?: boolean;
  hideCount?: boolean;
  newSelect?: boolean;
};

export function mapAudienceAttribute({
  name = '',
  audienceId,
  ...rest
}: CampaignAudienceAttribute) {
  return {
    ...rest,
    label: name,
    id: audienceId,
  } as Option;
}

export function mapAudience({
  name: label,
  id,
  contactsCount,
}: AudiencesModel) {
  return {
    label,
    id,
    contactsCount,
  } as Option;
}

export const AudiencesSelector = ({
  disabled,
  label,
  className,
  selected,
  onClose,
  onChange,
  withThreeDotMenu = false,
  placeholder,
  onlyEsAudiences,
  singleSelect,
  hideCount,
  newSelect,
}: Props) => {
  const [audienceId, setAudienceId] = useState(0);
  const { state, dispatcher } = useAudiencesReducer();
  const { onEdit } = useAudienceViewHandlers();
  const { busy, pagination, entities } = state;

  const options = entities
    .filter(entity => (onlyEsAudiences ? entity.type === 'es_based' : true))
    .map(mapAudience);

  const onOpen = () => dispatcher.retrieve();

  const onCloseLocal = () => {
    // prevent debounced search from firing after select closed
    onClose?.();
    onSearch.cancel();
    dispatcher.reset();
  };

  const onScrollEnd =
    pagination?.isLastPage || busy ? undefined : dispatcher.retrieve;

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (
      target.scrollTop + target.clientHeight >= target.scrollHeight - 20 &&
      !pagination?.isLastPage &&
      !busy
    ) {
      dispatcher.retrieve();
    }
  };

  const searchDebounced = debounce(dispatcher.search, 400);

  const onSearch = useRef<typeof searchDebounced>(searchDebounced).current;
  const elements: MenuItem[] = [{ name: 'Edit', value: 'edit' }];

  const menuProps = {
    className: 'drip-audience-selector__three-dot-menu',
    contentClassName: 'drip-audience-selector__dropdown_content',
    elements,
    selectItem: (val?: string) => {
      switch (val) {
        case 'edit':
          if (audienceId > 0) {
            return onEdit(audienceId);
          }
          break;
        default:
          break;
      }
    },
  };

  const selectComponent = newSelect ? (
    <AntdSelect
      className={className}
      placeholder={placeholder ?? 'Select Audiences'}
      value={
        selected?.map(opt => ({ label: opt.label, value: opt.id })) ?? undefined
      }
      disabled={disabled}
      options={options.map(({ label, id }) => ({ label, value: id }))}
      onChange={(value, option) => {
        const selectedOptions = Array.isArray(option) ? option : [option];
        onChange?.(
          selectedOptions.map(opt => ({ label: opt.label, id: opt.value })),
        );
      }}
      mode={singleSelect ? undefined : 'multiple'}
      loading={busy}
      onDropdownVisibleChange={open => (open ? onOpen() : onCloseLocal())}
      onSearch={onSearch}
      getPopupContainer={element => element.parentElement ?? document.body}
      dropdownRender={menu => <div onScroll={handlePopupScroll}>{menu}</div>}
    />
  ) : (
    <Select
      className={className}
      label={label}
      placeholder={placeholder ?? 'Select Audiences'}
      selected={selected}
      disabled={disabled}
      options={options}
      onOpen={onOpen}
      onClose={onCloseLocal}
      onChange={onChange}
      onSearch={onSearch}
      onScrollEnd={onScrollEnd}
      isLoading={busy}
      withThreeDotMenu={withThreeDotMenu}
      singleSelect={singleSelect}
      hideCount={hideCount}
      setAudienceId={setAudienceId}
      disableClickOutsideExceptions
    />
  );

  if (disabled && selected?.length) {
    const text = selected.map(({ label }) => label).join(', ');
    return (
      <Tooltip title={text} placement="bottom">
        {selectComponent}
      </Tooltip>
    );
  }

  return selectComponent;
};
